import { Add } from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CardHeader,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { ChangeEvent, FC, useState } from "react";
import { applyPagination } from "src/Helpers/utils";
import { IPayableItem } from "src/Types/module/paymentableItem/itemList";
import { TableLoader, TableNotFound } from "src/Utils/TableLoader";
import CreatePayableItem from "../CreatePaymentableItem/Index";
import PayableItemTableRow from "./PaymentableItemTableRow";

interface RecentOrdersTableProps {
	className?: string;
	payableItems: IPayableItem[];
	isLoading: boolean;
}

const PayableItemTable: FC<RecentOrdersTableProps> = ({
	payableItems,
	isLoading,
}) => {
	const [page, setPage] = useState<number>(0);
	const [limit, setLimit] = useState<number>(25);

	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

	const handlePageChange = (event: any, newPage: number): void => {
		setPage(newPage);
	};

	const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
		setLimit(parseInt(event.target.value));
	};

	const paginatedData = applyPagination(payableItems, page, limit);

	const [open, setOpen] = useState(false);
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	return (
		<Card>
			<CardHeader
				title="Service Items List"
				action={
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							gap: 2,
						}}
					>
						<Button
							onClick={() => handleOpen()}
							variant="contained"
							startIcon={<Add />}
						>
							Create Item
						</Button>
					</Box>
				}
			/>
			<Divider />
			<TableContainer>
				<Table sx={{ position: "relative", minHeight: "250px" }}>
					<TableHead>
						<TableRow>
							<TableCell>SL.</TableCell>
							<TableCell>Name</TableCell>
							<TableCell>Amount</TableCell>
							<TableCell>Discount</TableCell>
							<TableCell>Date</TableCell>
							<TableCell>Type</TableCell>
							<TableCell>Created by</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					{isLoading ? (
						<TableLoader />
					) : paginatedData.length ? (
						<TableBody>
							{paginatedData.map((payableItem, index) => (
								<PayableItemTableRow
									payableItem={payableItem}
									key={payableItem.id}
									index={index}
								/>
							))}
						</TableBody>
					) : (
						<TableNotFound />
					)}
				</Table>
			</TableContainer>
			<Box p={2}>
				<TablePagination
					component="div"
					count={payableItems.length}
					onPageChange={handlePageChange}
					onRowsPerPageChange={handleLimitChange}
					page={page}
					rowsPerPage={limit}
					rowsPerPageOptions={[25, 50, 75, 100]}
				/>
			</Box>

			{/* Modal */}
			<Dialog
				fullScreen={fullScreen}
				open={open}
				maxWidth="md"
				onClose={handleClose}
				aria-labelledby="responsive-dialog-title"
			>
				<DialogContent>
					<Box sx={{ mb: 2 }}>
						<Typography sx={{ mb: 2 }}>
							Create Service Item
						</Typography>
					</Box>
					<CreatePayableItem handleClose={handleClose} />
				</DialogContent>
				<DialogActions>
					<Button autoFocus onClick={handleClose}>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
};

export default PayableItemTable;
